import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import { LoadingContext } from '../../composants/loadingContext';
import Loader from '../../composants/loader/Loader';
import axiosInstance from '../../configurations/axiosInstance/AxiosInstance';
import { isUserConnect, useTitle } from '../../configurations/utils/Utils';
import { toastClick } from '../../composants/toast/CustomToast';
import { formatAuthor, formatTitle } from '../../composants/utils/utils';
import { Empty, Tag, Spin, Modal, Progress } from 'antd';
import {Editor} from "react-draft-wysiwyg";
import {convertFromRaw, EditorState} from "draft-js";
import { 
    faBugs, 
    faCheckCircle, 
    faCircle, 
    faClose, 
    faL, 
    faSpinner, 
    faTimesCircle,
    faArrowLeft,
    faArrowRight,
    faBars,
    faTrophy,
    faGraduationCap,
    faBook,
    faChevronRight
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LexicalViewer from '../editor/LexicalViewer';

const CoursesView = () => {
    useTitle('Formation');
    isUserConnect();
    const {accessToken, droits, user} = useSelector(state => state.authUser);
    const {id} = useParams();
    const [menu, setMenu] = useState(true);
    const [showQuiz, setShowQuiz] = useState(false);
    const [quizData, setQuizData] = useState(null);
    const [quizSubmit, setQuizSubmit] = useState(null);

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [selectedAnswers, setSelectedAnswers] = useState({});
    const [quizSubmitted, setQuizSubmitted] = useState(false);
    const [quizAttemptId, setQuizAttemptId] = useState(null);
    const [quizType, setQuizType] = useState(null); // 'module' ou 'formation'
    const [showExplanation, setShowExplanation] = useState(false);
    const [isAnswerValid, setIsAnswerValid] = useState(null); // true = correct, false = incorrect, null = not validated
    const [formation, setFormation] = useState(null);
    const [allChapter, setAllChapter] = useState([]);
    const [enrollment, setEnrollment] = useState(null);
    const [detailedProgress, setDetailedProgress] = useState(null);
    const [currentModule, setCurrentModule] = useState(null);
    const [currentChapter, setCurrentChapter] = useState(null);
    const {loading, setLoading} = useContext(LoadingContext);
    const [attemptData, setAttemptData] = useState(null);

    // Charger ou créer l'enrollment
    const initializeEnrollment = async () => {
        setLoading(true);
        try {
            // Vérifier si l'utilisateur a déjà un enrollment pour cette formation
            const response = await axiosInstance(accessToken).get(`api/learn/enrollments/formation/${id}/my-enrollment`);
            setEnrollment(response.data);
            await loadDetailedProgress(response.data.id);
        } catch (error) {
            if (error.response?.status === 404) {
                // Pas d'enrollment existant, en créer un
                try {
                    const enrollResponse = await axiosInstance(accessToken).post(`api/learn/enrollments/enroll?id=${id}`);
                    setEnrollment(enrollResponse.data);
                    await loadDetailedProgress(enrollResponse.data.id);
                } catch (enrollError) {
                    toastClick("Erreur lors de l'inscription à la formation", "error");
                    setLoading(false);
                }
            } else {
                toastClick("Erreur lors du chargement de votre progression", "error");
                setLoading(false);
            }
        }
    };

    // Charger la progression détaillée
    const loadDetailedProgress = async (enrollmentId) => {
        try {
            const response = await axiosInstance(accessToken).get(`api/learn/enrollments/${enrollmentId}/detailed-progress`);
            setDetailedProgress(response.data);
            
            // Trouver le premier chapitre non complété
            const firstIncompleteChapter = findFirstIncompleteChapter(response.data.formation);
            if (firstIncompleteChapter) {
                setCurrentModule(firstIncompleteChapter.module);
                setCurrentChapter(firstIncompleteChapter.chapter);
            }
            setLoading(false);
        } catch (error) {
            toastClick("Erreur lors du chargement de la progression", "error");
            setLoading(false);
        }
    };

    // Trouver le premier chapitre non complété
    const findFirstIncompleteChapter = (formationProgress) => {
        for (const module of formationProgress.modules) {
            for (const chapter of module.chapitres) {
                if (!chapter.completed) {
                    return { module, chapter };
                }
            }
        }
        // Si tous les chapitres sont complétés, retourner le dernier
        const lastModule = formationProgress.modules[formationProgress.modules.length - 1];
        const lastChapter = lastModule?.chapitres[lastModule.chapitres.length - 1];
        return lastModule && lastChapter ? { module: lastModule, chapter: lastChapter } : null;
    };

    const fetchFormation = ()=>{
        setLoading(true);
        axiosInstance(accessToken).get('public/api/learn/formations/published/'+id)
            .then(function (response) {
                setFormation(response.data);
                setAllChapter(response.data.modules.flatMap(module => module.chapitres));
                console.log("Formation data:", response.data);
                initializeEnrollment();
            })
            .catch(function (error) {
                setLoading(false);
                if (!error.response) {
                    toastClick("Impossible de charger la formation. Vérifiez votre connexion Internet.", "error");
                } else if (error.response.status === 404) {
                    toastClick("Formation introuvable", "error");
                } else if (error.response.status === 403) {
                    toastClick("Vous n'avez pas accès à cette formation", "error");
                } else {
                    toastClick("Erreur lors du chargement de la formation", "error");
                }
            });
    };

    // Marquer un chapitre comme complété
    const markChapterComplete = async (chapterId) => {
        if (!enrollment) return;
        
        try {
            await axiosInstance(accessToken).post('api/learn/enrollments/chapter/complete', {
                enrollmentId: enrollment.id,
                chapterId: chapterId,
                timeSpentSeconds: 0
            }).then(async (response) => {
                toastClick("Chapitre terminé !", "success");
                await loadDetailedProgress(enrollment.id);
            },
            (error)=>{
                toastClick("Erreur lors de la sauvegarde de la progression", "error");
            }
        );
            
            
        } catch (error) {
            console.error("Erreur lors de la sauvegarde de la progression", error);
        }
    };

    // Naviguer vers le chapitre suivant
    const nextChapter = async () => {
        if (!currentChapter || !enrollment) return;
        
        setLoading(true);
        
        // Marquer le chapitre actuel comme complété
        await markChapterComplete(currentChapter.id);
        
        // Trouver le prochain chapitre
        const nextChap = findNextChapter();
        if (nextChap) {
            setCurrentModule(nextChap.module);
            setCurrentChapter(nextChap.chapter);
        }
        
        setLoading(false);
    };

    // Trouver le chapitre suivant
    const findNextChapter = () => {
        if (!detailedProgress || !currentModule || !currentChapter) return null;
        
        const modules = detailedProgress.formation.modules;
        const currentModuleIndex = modules.findIndex(m => m.id === currentModule.id);
        const currentModuleData = modules[currentModuleIndex];
        const currentChapterIndex = currentModuleData.chapitres.findIndex(c => c.id === currentChapter.id);
        
        // Y a-t-il un chapitre suivant dans le module actuel ?
        if (currentChapterIndex < currentModuleData.chapitres.length - 1) {
            return {
                module: currentModuleData,
                chapter: currentModuleData.chapitres[currentChapterIndex + 1]
            };
        }
        
        // Y a-t-il un module suivant ?
        if (currentModuleIndex < modules.length - 1) {
            const nextModule = modules[currentModuleIndex + 1];
            return {
                module: nextModule,
                chapter: nextModule.chapitres[0]
            };
        }
        
        // Tous les chapitres sont terminés
        return null;
    };

    // Trouver le chapitre précédent
    const findPreviousChapter = () => {
        if (!detailedProgress || !currentModule || !currentChapter) return null;
        
        const modules = detailedProgress.formation.modules;
        const currentModuleIndex = modules.findIndex(m => m.id === currentModule.id);
        const currentModuleData = modules[currentModuleIndex];
        const currentChapterIndex = currentModuleData.chapitres.findIndex(c => c.id === currentChapter.id);
        
        // Y a-t-il un chapitre précédent dans le module actuel ?
        if (currentChapterIndex > 0) {
            return {
                module: currentModuleData,
                chapter: currentModuleData.chapitres[currentChapterIndex - 1]
            };
        }
        
        // Y a-t-il un module précédent ?
        if (currentModuleIndex > 0) {
            const previousModule = modules[currentModuleIndex - 1];
            return {
                module: previousModule,
                chapter: previousModule.chapitres[previousModule.chapitres.length - 1]
            };
        }
        
        // C'est le premier chapitre
        return null;
    };

    // Naviguer vers le chapitre précédent
    const previousChapter = () => {
        const prevChap = findPreviousChapter();
        if (prevChap) {
            setCurrentModule(prevChap.module);
            setCurrentChapter(prevChap.chapter);
        }
    };

    // Cliquer sur un chapitre dans le menu
    const onChapterClick = (module, chapter) => {
        // Empêcher la navigation si un quiz est en cours
        if (showQuiz) {
            toastClick("Veuillez terminer le quiz en cours avant de naviguer", "warning");
            return;
        }
        
        if(chapter.completed){
            setCurrentModule(module);
            setCurrentChapter(chapter);
        }
    };

    // Démarrer le quiz de module
    const doQuizz = async () => {
        if (!currentModule || !currentModule.withQuiz) {
            toastClick("Aucun quiz disponible pour ce module", "info");
            return;
        }
        
        setLoading(true);
        try {
            // Charger les informations du quiz via l'API module
            const quizResponse = await axiosInstance(accessToken).get(`api/learn/quiz/module/${currentModule.id}`);
            const quizInfo = quizResponse.data;
            console.log("Quiz Info:", quizInfo);
            
            // Vérifier si l'utilisateur peut passer le quiz
            const canAttemptResponse = await axiosInstance(accessToken).get(
                `api/learn/quiz/${quizInfo.id}/can-attempt`
            );
            
            if (!canAttemptResponse.data) {
                toastClick("Vous avez atteint le nombre maximum de tentatives", "warning");
                setLoading(false);
                return;
            }
            
            // Démarrer une tentative de quiz
            const attemptResponse = await axiosInstance(accessToken).post(
                `api/learn/quiz/attempts/start?quizId=${quizInfo.id}&enrollmentId=${enrollment.id}`
            );
            
            setQuizAttemptId(attemptResponse.data.id);
            
            // Mélanger aléatoirement les questions
            const shuffledQuestions = [...quizInfo.questions].sort(() => Math.random() - 0.5);
            
            setQuizData({
                ...quizInfo,
                questions: shuffledQuestions
            });
            setQuizType('module');
            setShowQuiz(true);
            setCurrentQuestionIndex(0);
            setSelectedAnswers({});
            setQuizSubmitted(false);
            setIsAnswerValid(null);
            setShowExplanation(false);
            setQuizSubmit({
                attemptId: attemptResponse.data.id,
                answers: []
            });
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toastClick(error.response?.data?.message || "Erreur lors du chargement du quiz", "error");
        }
    };

    // Démarrer le quiz de formation
    const doFormationQuizz = async () => {
        if (!formation || !formation.withFinalQuiz || !formation.quiz) {
            toastClick("Aucun quiz final disponible pour cette formation", "info");
            return;
        }
        
        setLoading(true);
        try {
            // Vérifier si l'utilisateur peut passer le quiz
            const canAttemptResponse = await axiosInstance(accessToken).get(
                `api/learn/quiz/${formation.quiz.id}/can-attempt`
            );
            
            if (!canAttemptResponse.data) {
                toastClick("Vous avez atteint le nombre maximum de tentatives", "warning");
                setLoading(false);
                return;
            }
            
            // Démarrer une tentative de quiz
            const attemptResponse = await axiosInstance(accessToken).post(
                `api/learn/quiz/attempts/start?quizId=${formation.quiz.id}&enrollmentId=${enrollment.id}`
            );
            
            setQuizAttemptId(attemptResponse.data.id);
            
            // Charger les questions du quiz
            const quizResponse = await axiosInstance(accessToken).get(`api/learn/quiz/${formation.quiz.id}`);
            setQuizData(quizResponse.data);
            setQuizType('formation');
            setShowQuiz(true);
            setCurrentQuestionIndex(0);
            setSelectedAnswers({});
            setQuizSubmitted(false);
            setIsAnswerValid(null);
            setShowExplanation(false);
            setQuizSubmit({
                attemptId: attemptResponse.data.id,
                answers: []
            });
            setLoading(false);
        } catch (error) {
            setLoading(false);
            toastClick(error.response?.data?.message || "Erreur lors du chargement du quiz final", "error");
        }
    };

    // Gérer la sélection d'une réponse
    const handleAnswerSelect = (questionId, answerId, isSingleChoice, question) => {
        if (isSingleChoice) {
            // Choix unique : remplacer la réponse
            const newSelectedAnswers = {
                ...selectedAnswers,
                [questionId]: [answerId]
            };
            setSelectedAnswers(newSelectedAnswers);
            
            // Vérifier si c'est la bonne réponse
            const selectedOption = question.options.find(opt => opt.id === answerId);

            // Construire l'objet SubmitQuizDTO
            const quizAnswers = Object.entries(newSelectedAnswers).flatMap(([qId, answerIds]) => {
                const answerArray = Array.isArray(answerIds) ? answerIds : [answerIds];
                return answerArray.map(optionId => ({
                    questionId: qId,
                    selectedOptionId: optionId
                }));
            });

            setQuizSubmit({
                attemptId: quizAttemptId,
                answers: quizAnswers
            });

            if (selectedOption && selectedOption.isCorrect) {
                setIsAnswerValid(true);
                setShowExplanation(false);
            } else {
                setIsAnswerValid(false);
                setShowExplanation(true);
            }
        } else {
            // Choix multiple : ajouter/retirer la réponse
            const currentAnswers = selectedAnswers[questionId] || [];
            const answerIndex = currentAnswers.indexOf(answerId);
            
            let newAnswers;
            if (answerIndex > -1) {
                // Retirer la réponse
                newAnswers = currentAnswers.filter(id => id !== answerId);
            } else {
                // Ajouter la réponse
                newAnswers = [...currentAnswers, answerId];
            }
            
            const newSelectedAnswers = {
                ...selectedAnswers,
                [questionId]: newAnswers.length > 0 ? newAnswers : undefined
            };
            setSelectedAnswers(newSelectedAnswers);

            // Construire l'objet SubmitQuizDTO
            const quizAnswers = Object.entries(newSelectedAnswers)
                .filter(([_, answerIds]) => answerIds !== undefined)
                .flatMap(([qId, answerIds]) => {
                    const answerArray = Array.isArray(answerIds) ? answerIds : [answerIds];
                    return answerArray.map(optionId => ({
                        questionId: qId,
                        selectedOptionId: optionId
                    }));
                });

            setQuizSubmit({
                attemptId: quizAttemptId,
                answers: quizAnswers
            });
            
            // Vérifier si toutes les bonnes réponses sont sélectionnées
            if (newAnswers.length > 0) {
                const allCorrectSelected = areAllCorrectAnswersSelected({
                    ...question,
                    id: questionId
                }, newAnswers);
                
                if (allCorrectSelected) {
                    setIsAnswerValid(true);
                    setShowExplanation(false);
                } else {
                    // Vérifier si une réponse incorrecte est sélectionnée
                    const hasIncorrectAnswer = newAnswers.some(id => {
                        const option = question.options.find(opt => opt.id === id);
                        return option && !option.isCorrect;
                    });
                    
                    setIsAnswerValid(false);
                    setShowExplanation(hasIncorrectAnswer);
                }
            } else {
                setIsAnswerValid(null);
                setShowExplanation(false);
            }
        }
    };

    // Vérifier si toutes les bonnes réponses sont sélectionnées (pour choix multiples)
    const areAllCorrectAnswersSelected = (question, answersArray = null) => {
        const selectedIds = answersArray !== null ? answersArray : (selectedAnswers[question.id] || []);
        
        // Obtenir toutes les options correctes
        const correctOptions = question.options.filter(opt => opt.isCorrect);
        
        // Vérifier que toutes les réponses correctes sont sélectionnées
        const allCorrectSelected = correctOptions.every(opt => selectedIds.includes(opt.id));
        
        // Vérifier qu'aucune réponse incorrecte n'est sélectionnée
        const noIncorrectSelected = selectedIds.every(id => {
            const option = question.options.find(opt => opt.id === id);
            return option && option.isCorrect;
        });
        
        return allCorrectSelected && noIncorrectSelected && selectedIds.length > 0;
    };

    // Soumettre le quiz
    const submitQuiz = async () => {
        if (!quizSubmit || !quizSubmit.attemptId) {
            toastClick("Erreur: données de quiz invalides", "error");
            return;
        }
        
        setLoading(true);
        try {
            
            // Soumettre le quiz avec l'objet SubmitQuizDTO
            const response = await axiosInstance(accessToken).post('api/learn/quiz/attempts/submit', quizSubmit);
            
            setQuizSubmitted(true);
            setAttemptData(response.data);
            
            // Recharger la progression
            await loadDetailedProgress(enrollment.id);
            
            if (response.data.passed) {
                toastClick("Félicitations ! Vous avez réussi le quiz !", "success");
            } else {
                toastClick("Quiz terminé. Vous pouvez réessayer si nécessaire.", "info");
            }
            
            setLoading(false);
        } catch (error) {
            setLoading(false);
            console.error("Quiz submission error:", error);
            toastClick("Erreur lors de la soumission du quiz", "error");
        }
    };

    // Calcul de la progression
    const getProgression = () => {
        if (!detailedProgress) return "0.00";
        return (detailedProgress.formation.progressionPourcent || 0).toFixed(2);
    };

    // Vérifier si tous les chapitres sont complétés
    const areAllChaptersCompleted = () => {
        if (!detailedProgress) return false;
        
        for (const module of detailedProgress.formation.modules) {
            for (const chapter of module.chapitres) {
                if (!chapter.completed) return false;
            }
        }
        return true;
    };

    // Vérifier si le module actuel est terminé (chapitres + quiz si présent)
    const isCurrentModuleCompleted = () => {
        if (!currentModule || !detailedProgress) return false;
        
        const moduleProgress = detailedProgress.formation.modules.find(m => m.id === currentModule.id);
        return moduleProgress?.completed || false;
    };

    // Vérifier si on est au dernier chapitre du module actuel
    const isLastChapterOfModule = () => {
        if (!currentModule || !currentChapter) return false;
        
        const lastChapter = currentModule.chapitres[currentModule.chapitres.length - 1];
        return currentChapter.id === lastChapter.id;
    };

    // Vérifier si tous les chapitres du module actuel sont complétés
    const areAllModuleChaptersCompleted = () => {
        if (!currentModule) return false;
        
        for (const chapter of currentModule.chapitres) {
            if (!chapter.completed) return false;
        }
        return true;
    };

    const compare = (a,b)=>{
        if(a.id<b.id)
            return -1;
        if(a.id>b.id)
            return 1;
        return 0;
    };
    
    useEffect(
        ()=> {
            fetchFormation();
        },[]
    );

    // Fonction de rendu du contenu du chapitre
    const renderChapterContent = () => {
        if (!currentChapter || !currentModule) {
            return <Empty description="Aucun contenu disponible" />;
        }

        return (
            <Spin spinning={loading}>
                <div>
                    <div className="mb-6">
                        <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            Module {currentModule.ordre} - Chapitre {currentChapter.ordre}
                        </div>
                        <h1 className="text-3xl font-bold text-gray-900 mb-4">
                            {currentChapter.titre}
                        </h1>
                        {/* {currentChapter.dureeEstimee && (
                            <div className="text-gray-600 mb-4">
                                <FontAwesomeIcon icon={faBook} className="mr-2" />
                                Durée estimée: {currentChapter.dureeEstimee} minutes
                            </div>
                        )} */}
                    </div>
                    <div className="prose prose-lg max-w-none mb-8">
                        <LexicalViewer 
                            content={allChapter.filter(c=>c.id===currentChapter.id)[0].contenu}
                            showCard={false}
                        />
                    </div>
                    
                    <div className="flex justify-between items-center mt-8 pt-6 border-t border-gray-200">
                        <div>
                            {findPreviousChapter() && (
                                <button
                                    type="button"
                                    onClick={() => previousChapter()}
                                    className="inline-flex items-center gap-3 bg-gray-600 text-white font-bold py-4 px-8 rounded-xl hover:bg-gray-700 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                    <FontAwesomeIcon icon={faArrowLeft} />
                                    <span>Chapitre Précédent</span>
                                </button>
                            )}
                        </div>
                        {areAllChaptersCompleted() ? (
                            currentModule.withQuiz && !isCurrentModuleCompleted() ? (
                                <button 
                                    type="button" 
                                    onClick={() => doQuizz()} 
                                    className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-4 px-8 rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                    <FontAwesomeIcon icon={faTrophy} className="text-xl" />
                                    <span>Passer le Quiz du Module {currentModule.titre}</span>
                                </button>
                            ) : formation.withFinalQuiz && !detailedProgress.formation.completed ? (
                                <button 
                                    type="button" 
                                    onClick={() => doFormationQuizz()} 
                                    className="inline-flex items-center gap-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-bold py-4 px-8 rounded-xl hover:from-purple-600 hover:to-pink-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                    <FontAwesomeIcon icon={faGraduationCap} className="text-xl" />
                                    <span>Passer le Quiz Final</span>
                                </button>
                            ) : (
                                <button 
                                    type="button" 
                                    onClick={() => window.location.href = '/'} 
                                    className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold py-4 px-8 rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                    <FontAwesomeIcon icon={faGraduationCap} className="text-xl" />
                                    <span>Formation Terminée</span>
                                </button>
                            )
                        ) : (
                            // Si on est au dernier chapitre du module et que le module a un quiz
                            isLastChapterOfModule() && currentModule.withQuiz && areAllModuleChaptersCompleted() ? (
                                <button 
                                    type="button" 
                                    onClick={() => {
                                        doQuizz();
                                    }} 
                                    className="inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-bold py-4 px-8 rounded-xl hover:from-yellow-600 hover:to-orange-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                                >
                                    <FontAwesomeIcon icon={faTrophy} className="text-xl" />
                                    <span>Passer le Quiz du Module  {currentModule.titre}</span>
                                </button>
                            ) : (
                                <button
                                    type="button"
                                    onClick={() => nextChapter()}
                                    className="inline-flex items-center gap-3 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold py-4 px-8 rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    <span>Chapitre Suivant</span>
                                    <FontAwesomeIcon icon={faChevronRight} />
                                </button>
                            )
                        )}
                    </div>
                </div>
            </Spin>
        );
    };

    // Fonction de rendu du quiz
    const renderQuiz = () => {
        if (!quizData || !quizData.questions) {
            return <Empty description="Aucune question disponible" />;
        }

        if (quizSubmitted) {
            // Récupérer les résultats depuis l'attempt
            const percentage = attemptData?.scoreObtenu || 0;
            const passed = attemptData?.passed || false;

            return (
                <div className="text-center py-12">
                    <div className={`inline-flex items-center justify-center w-32 h-32 rounded-full mb-6 ${passed ? 'bg-green-100' : 'bg-red-100'}`}>
                        <FontAwesomeIcon 
                            icon={passed ? faCheckCircle : faTimesCircle} 
                            className={`text-6xl ${passed ? 'text-green-500' : 'text-red-500'}`}
                        />
                    </div>
                    
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">
                        {passed ? 'Félicitations !' : 'Continuez vos efforts !'}
                    </h2>

                    {quizType === 'formation' && (
                        <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            Quiz Final de Formation
                        </div>
                    )}
                    
                    <p className="text-xl text-gray-600 mb-2">
                        Score: <span className="font-bold text-green-600">{attemptData?.pointsObtenus}</span> / <span className="font-bold">{attemptData?.pointsTotaux}</span>
                    </p>
                    
                    <p className="text-2xl font-bold mb-8" style={{color: passed ? '#10b981' : '#ef4444'}}>
                        {percentage.toFixed(0)}%
                    </p>

                    <Progress 
                        percent={percentage} 
                        strokeColor={passed ? '#10b981' : '#ef4444'}
                        className="mb-8 max-w-md mx-auto"
                    />

                    <div className="flex gap-4 justify-center">
                        <button
                            onClick={() => {
                                setShowQuiz(false);
                                setQuizSubmitted(false);
                                setCurrentQuestionIndex(0);
                                setSelectedAnswers({});
                                setQuizType(null);
                                setIsAnswerValid(null);
                                setShowExplanation(false);
                                setQuizSubmit(null);
                                setAttemptData(null);
                            }}
                            className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-blue-500 text-white font-bold py-3 px-6 rounded-xl hover:from-green-600 hover:to-blue-600 transition-all duration-300 shadow-lg"
                        >
                            <FontAwesomeIcon icon={faBook} />
                            <span>Retour au cours</span>
                        </button>

                        {!passed && (
                            <button
                                onClick={() => {
                                    setQuizSubmitted(false);
                                    setCurrentQuestionIndex(0);
                                    setSelectedAnswers({});
                                    setIsAnswerValid(null);
                                    setShowExplanation(false);
                                    setQuizSubmit(null);
                                    setAttemptData(null);
                                    if (quizType === 'formation') {
                                        doFormationQuizz();
                                    } else {
                                        doQuizz();
                                    }
                                }}
                                className="inline-flex items-center gap-2 bg-gray-600 text-white font-bold py-3 px-6 rounded-xl hover:bg-gray-700 transition-all duration-300 shadow-lg"
                            >
                                Réessayer
                            </button>
                        )}
                    </div>
                </div>
            );
        }

        const currentQuestion = quizData.questions[currentQuestionIndex];
        const isSingleChoice = currentQuestion.options && currentQuestion.options.length === 2;
        const selectedAnswersForQuestion = selectedAnswers[currentQuestion.id] || [];

        return (
            <div>
                <div className="mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-900">{quizData.titre}</h2>
                            {quizType === 'formation' && (
                                <div className="inline-block bg-purple-100 text-purple-700 px-3 py-1 rounded-full text-xs font-semibold mt-2">
                                    Quiz Final de Formation
                                </div>
                            )}
                            <div className="inline-block bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-xs font-semibold mt-2 ml-2">
                                {isSingleChoice ? 'Choix unique' : 'Choix multiples'}
                            </div>
                        </div>
                        <span className="text-sm font-semibold text-gray-600">
                            Question {currentQuestionIndex + 1} sur {quizData.questions.length}
                        </span>
                    </div>
                    
                    <Progress 
                        percent={((currentQuestionIndex + 1) / quizData.questions.length) * 100} 
                        showInfo={false}
                        strokeColor={{
                            '0%': quizType === 'formation' ? '#a855f7' : '#10b981',
                            '100%': quizType === 'formation' ? '#ec4899' : '#3b82f6',
                        }}
                    />
                </div>

                <div className={`${quizType === 'formation' ? 'bg-gradient-to-r from-purple-50 to-pink-50' : 'bg-gradient-to-r from-green-50 to-blue-50'} rounded-2xl p-8 mb-8`}>
                    <div className="flex items-start gap-4 mb-6">
                        <div className="flex-1">
                            <h3 className="text-xl font-bold text-gray-900 mb-2">
                                {currentQuestion.intitule}
                            </h3>
                        </div>
                        {currentQuestion.points && (
                            <div className="bg-white px-3 py-1 rounded-full text-sm font-semibold text-gray-700 border-2 border-gray-200">
                                {currentQuestion.points} pts
                            </div>
                        )}
                    </div>

                    {currentQuestion.imageUrl && (
                        <div className="mb-6">
                            <img 
                                src={currentQuestion.imageUrl} 
                                alt="Question" 
                                className="max-w-full h-auto rounded-lg shadow-md"
                            />
                        </div>
                    )}

                    {showExplanation && currentQuestion.explication && !isSingleChoice && (
                        <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                            <div className="flex items-start gap-3">
                                <FontAwesomeIcon icon={faTimesCircle} className="text-red-500 text-xl mt-1" />
                                <div>
                                    <p className="font-semibold text-red-800 mb-1">Attention !</p>
                                    <p className="text-sm text-red-700 italic">
                                        {currentQuestion.explication}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="space-y-4">
                        {currentQuestion.options && currentQuestion.options.map((option, index) => {
                            const isSelected = Array.isArray(selectedAnswersForQuestion) 
                                ? selectedAnswersForQuestion.includes(option.id)
                                : selectedAnswersForQuestion === option.id;
                            
                            // Déterminer la couleur en fonction de la validité
                            let borderColor = 'border-gray-200';
                            let bgColor = 'bg-white';
                            let iconBgColor = 'border-gray-300';
                            
                            if (isSelected) {
                                if (isAnswerValid === true) {
                                    // Réponse correcte - vert
                                    borderColor = 'border-green-500';
                                    bgColor = 'bg-green-50';
                                    iconBgColor = 'border-green-500 bg-green-500';
                                } else if (isAnswerValid === false) {
                                    // Réponse incorrecte - rouge
                                    borderColor = 'border-red-500';
                                    bgColor = 'bg-red-50';
                                    iconBgColor = 'border-red-500 bg-red-500';
                                } else {
                                    // Pas encore validé
                                    borderColor = quizType === 'formation' ? 'border-purple-500' : 'border-blue-500';
                                    bgColor = quizType === 'formation' ? 'bg-purple-50' : 'bg-blue-50';
                                    iconBgColor = quizType === 'formation' ? 'border-purple-500 bg-purple-500' : 'border-blue-500 bg-blue-500';
                                }
                            }
                            
                            return (
                                <div
                                    key={option.id}
                                    onClick={() => handleAnswerSelect(currentQuestion.id, option.id, isSingleChoice, currentQuestion)}
                                    className={`p-4 rounded-xl cursor-pointer transition-all duration-300 border-2 ${borderColor} ${bgColor} ${
                                        isSelected ? 'shadow-lg' : 'hover:border-green-300 hover:shadow-md'
                                    }`}
                                >
                                    <div className="flex items-center gap-4">
                                        <div className={`${isSingleChoice ? 'w-8 h-8 rounded-full' : 'w-8 h-8 rounded-md'} border-2 flex items-center justify-center ${iconBgColor}`}>
                                            {isSelected && (
                                                <FontAwesomeIcon icon={faCheckCircle} className="text-white" />
                                            )}
                                        </div>
                                        <span className="flex-1 font-medium text-gray-900">
                                            {option.texte} 
                                        </span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="flex justify-between items-center">
                    <button
                        onClick={() => {
                            setCurrentQuestionIndex(Math.max(0, currentQuestionIndex - 1));
                            setShowExplanation(false);
                            setIsAnswerValid(null);
                        }}
                        disabled={currentQuestionIndex === 0}
                        className={`inline-flex items-center gap-2 font-bold py-3 px-6 rounded-xl transition-all duration-300 ${
                            currentQuestionIndex === 0
                                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                : 'bg-gray-600 text-white hover:bg-gray-700 shadow-lg'
                        }`}
                    >
                        <FontAwesomeIcon icon={faArrowLeft} />
                        <span>Précédent</span>
                    </button>

                    {currentQuestionIndex === quizData.questions.length - 1 ? (
                        <button
                            onClick={submitQuiz}
                            disabled={Object.keys(selectedAnswers).length !== quizData.questions.length}
                            className={`inline-flex items-center gap-2 font-bold py-3 px-6 rounded-xl transition-all duration-300 ${
                                Object.keys(selectedAnswers).length !== quizData.questions.length
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : quizType === 'formation'
                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transform hover:scale-105'
                                        : 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-600 hover:to-blue-600 shadow-lg hover:shadow-xl transform hover:scale-105'
                            }`}
                        >
                            <FontAwesomeIcon icon={faTrophy} />
                            <span>Soumettre le Quiz</span>
                        </button>
                    ) : (
                        <button
                            onClick={() => {
                                setCurrentQuestionIndex(Math.min(quizData.questions.length - 1, currentQuestionIndex + 1));
                                setShowExplanation(false);
                                setIsAnswerValid(null);
                            }}
                            disabled={isAnswerValid !== true}
                            className={`inline-flex items-center gap-2 font-bold py-3 px-6 rounded-xl transition-all duration-300 ${
                                isAnswerValid !== true
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : quizType === 'formation'
                                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600 shadow-lg hover:shadow-xl transform hover:scale-105'
                                        : 'bg-gradient-to-r from-green-500 to-blue-500 text-white hover:from-green-600 hover:to-blue-600 shadow-lg hover:shadow-xl transform hover:scale-105'
                            }`}
                        >
                            <span>Suivant</span>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    )}
                </div>
            </div>
        );
    };

    // Helpers pour les icônes et couleurs du menu
    const getModuleIcon = (moduleProgress) => {
        if (moduleProgress.completed) return faCheckCircle;
        if (currentModule && moduleProgress.id === currentModule.id) return faSpinner;
        return faCircle;
    };

    const getModuleSpin = (moduleProgress) => {
        if (moduleProgress.completed) return false;
        return currentModule && moduleProgress.id === currentModule.id;
    };

    const getModuleColor = (moduleProgress) => {
        if (moduleProgress.completed) 
            return "w-6 h-6 rounded-full bg-white-500 items-center justify-center text-green-600";
        if (currentModule && moduleProgress.id === currentModule.id) 
            return 'w-6 h-6 rounded-full bg-white-500 items-center justify-center text-yellow-600';
        return "w-6 h-6 rounded-full bg-white-500 items-center justify-center text-gray-400";
    };

    const getChapterColor = (chapter) => {
        if (chapter.completed) 
            return "w-6 h-6 rounded-full bg-green-500 flex items-center justify-center text-white";
        if (currentChapter && chapter.id === currentChapter.id) 
            return "w-6 h-6 rounded-full bg-yellow-500 flex items-center justify-center text-white";
        return "w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center text-white";
    };

    // Rendu principal du composant
    return (
        <>
            {loading && <Loader />}
            
            {formation != null && detailedProgress != null && (
                <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
                    <div className="bg-white shadow-md border-b border-gray-200 sticky top-0 z-50">
                        <div className="max-w-7xl mx-auto px-4 py-4">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div className="flex items-center gap-4">
                                    <Link 
                                        to="/" 
                                        className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold py-2 px-6 rounded-xl shadow-lg hover:shadow-xl hover:from-green-600 hover:to-blue-600 transform hover:scale-105 transition-all duration-300"
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} />
                                        <span>Retour</span>
                                    </Link>
                                    
                                    <button 
                                        onClick={() => setMenu(!menu)}
                                        className="md:hidden inline-flex items-center gap-2 bg-blue-100 text-blue-600 font-semibold py-2 px-4 rounded-xl hover:bg-blue-200 transition-all duration-300"
                                    >
                                        <FontAwesomeIcon icon={menu ? faClose : faBars} />
                                        <span>{menu ? 'Fermer' : 'Menu'}</span>
                                    </button>

                                    <button 
                                        onClick={() => setMenu(!menu)}
                                        className="hidden md:inline-flex items-center gap-2 bg-green-100 text-green-600 font-semibold py-2 px-4 rounded-xl hover:bg-green-200 transition-all duration-300"
                                    >
                                        <FontAwesomeIcon icon={menu ? faClose : faBars} />
                                        <span>{menu ? 'Masquer Programme' : 'Afficher Programme'}</span>
                                    </button>
                                </div>

                                <div className="flex items-center gap-4 w-full md:w-auto">
                                    <div className="flex-1 md:w-64">
                                        <div className="flex items-center gap-3">
                                            <FontAwesomeIcon icon={faGraduationCap} className="text-green-500 text-xl" />
                                            <div className="flex-1">
                                                <div className="flex justify-between items-center mb-1">
                                                    <span className="text-sm font-semibold text-gray-700">Progression</span>
                                                    <span className="text-sm font-bold text-green-600">{getProgression()}%</span>
                                                </div>
                                                <Progress 
                                                    percent={parseFloat(getProgression())} 
                                                    showInfo={false}
                                                    strokeColor={{
                                                        '0%': '#10b981',
                                                        '100%': '#3b82f6',
                                                    }}
                                                    className="m-0"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="bg-gradient-to-r from-green-500 to-blue-500 py-8">
                        <div className="max-w-7xl mx-auto px-4">
                            <h1 
                                title={formation.titre} 
                                className="text-3xl md:text-4xl font-bold text-white text-center drop-shadow-lg"
                            >
                                {formation.titre}
                            </h1>
                        </div>
                    </div>

                    <div className="max-w-7xl mx-auto px-4 py-8">
                        <div className="flex flex-col lg:flex-row gap-6">
                            <div className={`${menu ? 'block' : 'hidden'} lg:${menu ? 'block' : 'hidden'} lg:w-80 flex-shrink-0 transition-all duration-300`}>
                                <div className="bg-white rounded-2xl shadow-xl p-6 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto">
                                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
                                        <div className="flex items-center gap-3">
                                            <FontAwesomeIcon icon={faBook} className="text-green-500 text-2xl" />
                                            <h2 className="text-xl font-bold text-gray-900">Programme</h2>
                                        </div>
                                        <button
                                            onClick={() => setMenu(false)}
                                            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <FontAwesomeIcon icon={faClose} className="text-gray-500" />
                                        </button>
                                    </div>
                                    
                                    <div className="space-y-4">
                                        {detailedProgress.formation.modules.map((mod, index) => (
                                            <div key={mod.id} className="border-l-4 border-gray-200 pl-4 hover:border-green-500 transition-colors">
                                                <div className="flex items-start gap-3 mb-3">
                                                    <div className="mt-1">
                                                        <FontAwesomeIcon 
                                                            className={getModuleColor(mod)} 
                                                            icon={getModuleIcon(mod)}  
                                                            spin={getModuleSpin(mod)}
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-xs font-semibold text-green-600 mb-1">
                                                            MODULE {mod.ordre}
                                                        </div>
                                                        <h3 
                                                            title={mod.titre} 
                                                            className="font-semibold text-gray-900 text-sm leading-tight"
                                                        >
                                                            {formatTitle(mod.titre, 20)}
                                                        </h3>
                                                        {mod.completed && (
                                                            <div className="mt-1">
                                                                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold">
                                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                                    Complété
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-2 ml-8">
                                                    {mod.chapitres && mod.chapitres.map((chap, chapIndex) => (
                                                        <div 
                                                            key={chap.id} 
                                                            className={`flex items-center gap-2 text-xs group p-2 rounded-lg transition-all ${
                                                                currentChapter && chap.id === currentChapter.id 
                                                                    ? 'bg-blue-50' 
                                                                    : showQuiz 
                                                                        ? 'opacity-50 cursor-not-allowed' 
                                                                        : 'hover:bg-gray-50 cursor-pointer'
                                                            }`}
                                                            title={showQuiz ? 'Terminez le quiz en cours' : chap.titre}
                                                            onClick={() => onChapterClick(mod, chap)}
                                                        >
                                                            <div className={`${getChapterColor(chap)} text-xs font-bold flex-shrink-0`}>
                                                                {chap.completed ? (
                                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                                ) : (
                                                                    chap.ordre
                                                                )}
                                                            </div>
                                                            <span className={`flex-1 transition-colors ${
                                                                chap.completed 
                                                                    ? 'text-green-600 font-medium' 
                                                                    : 'text-gray-600 group-hover:text-gray-900'
                                                            }`}>
                                                                {formatTitle(chap.titre, 25)}
                                                            </span>
                                                            {chap.completed && (
                                                                <FontAwesomeIcon 
                                                                    icon={faCheckCircle} 
                                                                    className="text-green-500 text-xs" 
                                                                />
                                                            )}
                                                        </div>
                                                    ))}
                                                    {mod.withQuiz && (
                                                        <div className={`flex items-center gap-2 text-xs group p-2 rounded-lg ${
                                                            mod.completed ? 'bg-yellow-50' : 'hover:bg-gray-50'
                                                        }`}
                                                            title="Quiz du module">
                                                            <FontAwesomeIcon 
                                                                icon={mod.completed ? faCheckCircle : faTrophy} 
                                                                className={mod.completed ? "text-green-500" : "text-yellow-500"} 
                                                            />
                                                            <span className={`transition-colors ${
                                                                mod.completed 
                                                                    ? 'text-green-600 font-medium' 
                                                                    : 'text-gray-600 group-hover:text-gray-900'
                                                            }`}>
                                                                Quiz du module
                                                            </span>
                                                            {mod.completed && (
                                                                <FontAwesomeIcon 
                                                                    icon={faCheckCircle} 
                                                                    className="text-green-500 text-xs" 
                                                                />
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                        
                                        {formation.withFinalQuiz && (
                                            <div className={`border-l-4 border-purple-300 pl-4 hover:border-purple-500 transition-colors mt-6 ${
                                                showQuiz ? 'opacity-50 cursor-not-allowed' : ''
                                            }`}>
                                                <div className="flex items-start gap-3 mb-3">
                                                    <div className="mt-1">
                                                        <FontAwesomeIcon 
                                                            className={detailedProgress.formation.completed 
                                                                ? "w-6 h-6 text-green-600" 
                                                                : "w-6 h-6 text-purple-600"
                                                            } 
                                                            icon={detailedProgress.formation.completed ? faCheckCircle : faGraduationCap}
                                                        />
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="text-xs font-semibold text-purple-600 mb-1">
                                                            QUIZ FINAL
                                                        </div>
                                                        <h3 className="font-semibold text-gray-900 text-sm leading-tight">
                                                            Quiz de Certification
                                                        </h3>
                                                        {detailedProgress.formation.completed && (
                                                            <div className="mt-1">
                                                                <span className="inline-flex items-center gap-1 text-xs text-green-600 font-semibold">
                                                                    <FontAwesomeIcon icon={faCheckCircle} />
                                                                    Réussi
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={`flex-1 transition-all duration-300 ${menu ? 'lg:mr-6' : 'lg:max-w-full'}`}>
                                {!showQuiz ? (
                                    <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8">
                                        {renderChapterContent()}
                                    </div>
                                ) : (
                                    <div className="bg-white rounded-2xl shadow-xl p-8 lg:p-12">
                                        {renderQuiz()}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default CoursesView;